<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import NewInput from "../forms/NewInput.vue";
import NewSelect from "../NewSelect.vue";
import NewAutoselect from "../forms/NewAutoselect.vue";
import SimpleLoadingCircle from "../SimpleLoadingCircle.vue";

const logoFileRef = ref();
const backgroundFileRef = ref();

const sportTypeMap = {
    "american-football": "American Football",
    basketball: "Basketball",
    soccer: "Soccer",
    hockey: "Hockey",
    "field-hockey": "Field Hockey",
    lacrosse: "Lacrosse",
    volleyball: "Volleyball",
};
const sportTypeIconMap = {
    "american-football": "american-football-outline",
    basketball: "basketball-outline",
    soccer: "football-outline",
    hockey: "ellipse",
    "field-hockey": "ellipse",
    lacrosse: "ellipse",
    volleyball: "ellipse",
};

const isLoading = ref(false);

const modal = reactive({
    isOpen: false,
    isGuideOpen: false,
});

const props = defineProps({
    action: String,
    csrf: String,
    noIcon: Boolean,
    plan: String,
    interval: String,
});

const state = reactive({
    logoPreview: "/default-images/team.png",
    backgroundPreview: "/default-images/team-background.png",
    backgroundFile: null,
    logoFile: null,
    stateOptions: [],
    stateValue: 0,
    cityOptions: [],
    cityValue: 0,
    sportType: "american-football",
    name: "",
});

const errors = reactive({
    name: null,
    logo: null,
    background: null,
    general: null,
});

function handleLogoChange(e) {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
        state.logoPreview = e.target.result;
        state.logoFile = file;
    };
}

function handleBackgroundChange(e) {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
        state.backgroundPreview = e.target.result;
        state.backgroundFile = file;
    };
}

function submit(e) {
    if (props.plan) {
        submitWithPlan(e);
        return;
    }

    submitFreeTeam(e);
}

function submitFreeTeam(e) {
    const data = new FormData(e.target);

    errors.name = null;
    errors.logo = null;
    errors.background = null;
    errors.general = null;

    isLoading.value = true;
    axios
        .post(props.action, data)
        .then((res) => {
            window.location.reload();
        })
        .catch((err) => {
            isLoading.value = false;
            if (err.response.status === 422) {
                errors.name = err.response.data.errors.name;
                errors.logo = err.response.data.errors.logo;
                errors.background = err.response.data.errors.background;
            } else {
                errors.general =
                    err.response.data.message ?? "Something went wrong";
            }
        });
}

function submitWithPlan(e) {
    const data = new FormData(e.target);
    data.append("plan", props.plan);
    data.append("interval", props.interval);

    errors.name = null;
    errors.logo = null;
    errors.background = null;
    errors.general = null;

    isLoading.value = true;
    axios
        .post(props.action, data)
        .then((res) => {
            window.location.href = res.data.redirect;
        })
        .catch((err) => {
            isLoading.value = false;
            if (err.response.status === 422) {
                errors.name = err.response.data.errors.name;
                errors.logo = err.response.data.errors.logo;
                errors.background = err.response.data.errors.background;
            } else {
                errors.general =
                    err.response.data.message ?? "Something went wrong";
            }
        });
}

function fetchStates() {
    const usCountryId = 233;
    return axios
        .get("/helper-function-controller/get-states/" + usCountryId)
        .then((res) => {
            state.stateOptions = res.data.map((state) => {
                return {
                    label: state.name,
                    value: state.id,
                };
            });
            state.stateValue = res.data[0].id;
        })
        .catch((err) => {
            console.log(err);
        });
}

function fetchCities() {
    return axios
        .get(`/helper-function-controller/get-cities-new/${state.stateValue}`)
        .then((res) => {
            state.cityOptions = res.data.map((city) => {
                return {
                    label: city.name,
                    value: city.id,
                };
            });
            if (res.data.length > 0) {
                state.cityValue = res.data[0].id;
            } else {
                state.cityOptions = [
                    {
                        label: "None",
                        value: -1,
                    },
                ];
                state.cityValue = -1;
            }
        })
        .catch((err) => {
            console.log(err);
        });
}

function clearLogoFile() {
    logoFileRef.value.value = "";
    state.logoPreview = "/default-images/team.png";
    state.logoFile = null;
}

function clearBackgroundFile() {
    backgroundFileRef.value.value = "";
    state.backgroundPreview = "/default-images/team-background.png";
    state.backgroundFile = null;
}

const isValid = computed(() => {
    const basicInfoValid = state.name.length > 0;
    let logoValid = true;
    const tenMb = 10000000;
    if (state.logoFile) {
        logoValid = state.logoFile.size < tenMb;
    }
    let backgroundValid = true;
    if (state.backgroundFile) {
        backgroundValid = state.backgroundFile.size < tenMb;
    }
    return basicInfoValid && logoValid && backgroundValid;
});

onMounted(() => {
    fetchStates().then(() => {
        fetchCities();
    });
});

async function handleOpenModal() {
    const resp = await axios.get("/teams/listApi");
    const data = resp.data;

    if (data?.length == 0) {
        modal.isGuideOpen = true;
    } else {
        modal.isOpen = true;
    }

    // modal.isOpen = true;
}

const handleOpen = () => {
    modal.isGuideOpen = false;
    modal.isOpen = true;
};
</script>

<template>
    <new-team-guide
        :is-open="modal.isGuideOpen"
        @close="modal.isGuideOpen = true"
        @open="handleOpen"
    ></new-team-guide>
    <button
        type="button"
        @click="handleOpenModal()"
        class="
            md:ml-auto
            flex
            py-1
            px-3
            border
            font-bold
            border-accent
            rounded-full
            text-accent
            items-center
            text-md
        "
        v-bind="$attrs"
    >
        <ion-icon name="add-outline" class="mr-1" v-if="!noIcon"></ion-icon>
        <slot> Add New Team </slot>
    </button>
    <teleport to="body">
        <div
            v-show="modal.isOpen"
            class="
                bg-black bg-opacity-25
                fixed
                top-0
                left-0
                w-full
                h-full
                z-[1000]
                flex
                items-center
                justify-center
            "
        >
            <form
                :action="props.action"
                method="post"
                @submit.prevent="submit($event)"
            >
                <input type="hidden" name="_token" :value="props.csrf" />
                <div
                    class="
                        bg-white
                        rounded-lg
                        shadow-lg
                        relative
                        font-sans
                        min-w-[60vw]
                        max-w-[90vw]
                    "
                >
                    <div class="flex items-start flex-col md:flex-row">
                        <div
                            class="
                                flex-grow-0 flex-shrink-0
                                w-[320px]
                                gap-2
                                hidden
                                md:block
                            "
                        >
                            <div>
                                <img
                                    :src="state.backgroundPreview"
                                    class="
                                        w-full
                                        h-28
                                        rounded-tl-lg
                                        object-cover
                                    "
                                    alt="Team Background"
                                />
                                <img
                                    :src="state.logoPreview"
                                    alt="Team Logo"
                                    class="
                                        w-32
                                        h-32
                                        rounded-full
                                        border-2 border-solid border-gray-300
                                        aspect-square
                                        mx-auto
                                        -mt-16
                                        object-cover
                                    "
                                />
                            </div>
                            <div class="mt-4 text-gray-400 px-4 text-center">
                                {{
                                    state.cityOptions.find(
                                        (city) => city.value === state.cityValue
                                    )?.label
                                }}
                            </div>
                            <div
                                class="
                                    mt-1
                                    text-xl
                                    font-semibold
                                    px-4
                                    text-center
                                "
                            >
                                {{ state.name }}
                            </div>
                            <div class="px-4 mx-auto mt-4">
                                <div
                                    class="
                                        flex
                                        items-center
                                        gap-2
                                        bg-red-100
                                        text-red-900
                                        font-[600]
                                        text-sm
                                        rounded-lg
                                        py-1
                                        px-2
                                        w-[fit-content]
                                        mx-auto
                                    "
                                >
                                    <ion-icon
                                        :name="
                                            sportTypeIconMap[state.sportType]
                                        "
                                    ></ion-icon>
                                    <span>{{
                                        sportTypeMap[state.sportType]
                                    }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="flex-grow flex-shrink w-full p-8">
                            <div class="flex items-center">
                                <h3
                                    class="
                                        flex-grow flex-shrink
                                        text-xl
                                        font-semibold
                                    "
                                >
                                    Create Team
                                </h3>
                                <button
                                    type="button"
                                    class="
                                        rounded-md
                                        ml-auto
                                        flex
                                        items-center
                                        justify-center
                                        p-1
                                        bg-gray-100
                                        hover:bg-gray-200
                                        text-xl text-gray-500
                                    "
                                    @click="modal.isOpen = false"
                                >
                                    <ion-icon name="close-outline"></ion-icon>
                                </button>
                            </div>
                            <h4 class="text-gray-400 my-2">
                                Let's start creating your team, just fill out
                                the information below.
                            </h4>
                            <hr class="bg-gray-500 my-6" />
                            <div class="max-h-[50vh]">
                                <div
                                    class="
                                        text-xs
                                        my-2
                                        p-2
                                        bg-red-100
                                        text-red-800
                                        rounded-lg
                                    "
                                    v-if="errors.logo"
                                >
                                    <div v-for="err in errors.logo">
                                        {{ err }}
                                    </div>
                                </div>
                                <div class="flex items-center gap-2">
                                    <img
                                        :src="state.logoPreview"
                                        class="
                                            border border-solid border-gray-300
                                            w-14
                                            h-14
                                            rounded-full
                                            object-cover
                                            aspect-square
                                        "
                                        alt="Team Logo"
                                    />
                                    <div>
                                        <input
                                            @change="handleLogoChange"
                                            class="hidden"
                                            type="file"
                                            ref="logoFileRef"
                                            name="logo"
                                            accept="image/jpeg,image/png"
                                        />
                                        <button
                                            @click="logoFileRef.click()"
                                            type="button"
                                            class="
                                                block
                                                text-primary
                                                font-semibold
                                            "
                                        >
                                            Change Image
                                        </button>
                                        <button
                                            @click="clearLogoFile"
                                            v-if="state.logoFile"
                                            type="button"
                                            class="
                                                block
                                                text-red-500
                                                font-semibold
                                            "
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>

                                <div class="my-4">
                                    <div
                                        class="
                                            text-xs
                                            my-2
                                            p-2
                                            bg-red-100
                                            text-red-800
                                            rounded-lg
                                        "
                                        v-if="errors.name"
                                    >
                                        <div v-for="err in errors.name">
                                            {{ err }}
                                        </div>
                                    </div>
                                    <label
                                        class="
                                            font-semibold
                                            block
                                            mb-2
                                            text-gray-700 text-sm
                                        "
                                        for="title"
                                        >Team Name</label
                                    >
                                    <NewInput
                                        type="text"
                                        name="name"
                                        placeholder="Enter the team name"
                                        required
                                        class="mb-4"
                                        v-model="state.name"
                                    />
                                </div>
                                <div class="my-4">
                                    <div
                                        class="
                                            text-xs
                                            my-2
                                            p-2
                                            bg-red-100
                                            text-red-800
                                            rounded-lg
                                        "
                                        v-if="errors.sport_type"
                                    >
                                        <div v-for="err in errors.sport_type">
                                            {{ err }}
                                        </div>
                                    </div>
                                    <label
                                        class="
                                            font-semibold
                                            block
                                            mb-2
                                            text-gray-700 text-sm
                                        "
                                        for="sport_type"
                                        >Team Sport</label
                                    >
                                    <new-select
                                        name="sport_type"
                                        prefix=""
                                        class="py-3"
                                        v-model="state.sportType"
                                        :options="sportTypeMap"
                                        :enableMaps="[
                                            'american-football',
                                            'basketball',
                                        ]"
                                    ></new-select>
                                </div>
                                <div class="grid grid-cols-2 my-4 gap-1">
                                    <div>
                                        <div
                                            class="
                                                text-xs
                                                my-2
                                                p-2
                                                bg-red-100
                                                text-red-800
                                                rounded-lg
                                            "
                                            v-if="errors.state"
                                        >
                                            <div v-for="err in errors.state">
                                                {{ err }}
                                            </div>
                                        </div>
                                        <label
                                            class="
                                                font-semibold
                                                block
                                                mb-2
                                                text-gray-700 text-sm
                                            "
                                            for="sport_type"
                                            >State</label
                                        >
                                        <NewAutoselect
                                            name="state"
                                            v-model="state.stateValue"
                                            @update:modelValue="fetchCities"
                                            :options="state.stateOptions"
                                        ></NewAutoselect>
                                    </div>
                                    <div>
                                        <div
                                            class="
                                                text-xs
                                                my-2
                                                p-2
                                                bg-red-100
                                                text-red-800
                                                rounded-lg
                                            "
                                            v-if="errors.city"
                                        >
                                            <div v-for="err in errors.city">
                                                {{ err }}
                                            </div>
                                        </div>
                                        <label
                                            class="
                                                font-semibold
                                                block
                                                mb-2
                                                text-gray-700 text-sm
                                            "
                                            for="city"
                                            >City</label
                                        >
                                        <NewAutoselect
                                            name="city"
                                            v-model="state.cityValue"
                                            @change="fetchCities"
                                            :options="state.cityOptions"
                                        ></NewAutoselect>
                                    </div>
                                </div>
                                <div class="my-4">
                                    <input
                                        @change="handleBackgroundChange"
                                        class="hidden"
                                        type="file"
                                        ref="backgroundFileRef"
                                        name="background"
                                        accept="image/jpeg,image/png"
                                    />
                                    <div
                                        class="
                                            text-xs
                                            my-2
                                            p-2
                                            bg-red-100
                                            text-red-800
                                            rounded-lg
                                        "
                                        v-if="errors.background"
                                    >
                                        <div v-for="err in errors.background">
                                            {{ err }}
                                        </div>
                                    </div>
                                    <label
                                        class="
                                            font-semibold
                                            block
                                            mb-2
                                            text-gray-700 text-sm
                                        "
                                        for="background"
                                        >Background Image</label
                                    >
                                    <div
                                        class="
                                            bg-gray-100
                                            rounded-lg
                                            py-3
                                            px-4
                                            flex
                                            items-center
                                        "
                                    >
                                        <div
                                            class="
                                                cursor-pointer
                                                flex-grow flex-shrink
                                                w-full
                                            "
                                            @click="backgroundFileRef.click()"
                                        >
                                            <span v-if="!state.backgroundFile">
                                                Default Image (click to change)
                                            </span>
                                            <span v-else>
                                                {{ state.backgroundFile.name }}
                                                ~{{
                                                    Math.ceil(
                                                        state.backgroundFile
                                                            .size / 1000
                                                    )
                                                }}
                                                KB
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            class="
                                                rounded-md
                                                ml-auto
                                                flex
                                                items-center
                                                justify-center
                                                p-1
                                                bg-gray-100
                                                hover:bg-gray-200
                                                text-xl text-gray-500
                                            "
                                            @click="clearBackgroundFile"
                                        >
                                            <ion-icon
                                                name="close-outline"
                                            ></ion-icon>
                                        </button>
                                    </div>
                                    <div class="text-gray-400 my-2 text-sm">
                                        Recommended size 800x400px - Max 15MB
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <footer
                        class="flex items-center justify-end p-8 gap-4"
                        v-if="!isLoading"
                    >
                        <button
                            type="button"
                            @click="modal.isOpen = false"
                            class="
                                bg-white
                                text-primary
                                border-solid border border-primary
                                rounded-full
                                py-3
                                px-6
                                hover:bg-accent hover:bg-opacity-10
                            "
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            :disabled="!isValid"
                            class="
                                bg-accent
                                text-white
                                border-solid border border-transparent
                                rounded-full
                                py-3
                                px-6
                                hover:bg-accent hover:bg-opacity-90
                                disabled:opacity-50
                            "
                        >
                            Create Team
                        </button>
                    </footer>
                    <footer
                        class="flex items-center justify-end p-8 gap-4"
                        v-else
                    >
                        <simple-loading-circle></simple-loading-circle>
                    </footer>
                </div>
            </form>
        </div>
    </teleport>
</template>

<style scoped></style>
